import React from 'react';
import EditableText from './EditableText';

/**
 * EditableTrustBlock - Trust/social proof section with inline editing support
 */
export default function EditableTrustBlock({
  sectionBadge = 'Trusted by Industry Leaders',
  stats = [
    { value: '25%', label: 'Average Tax Reduction' },
    { value: '1,200+', label: 'Businesses Protected' },
    { value: '$50M+', label: 'Total Client Savings' }
  ],
  partners = ['Pfister Financial', 'Strategic Tax Partners', 'Fidelity Investments'],
  guarantees = [
    {
      title: 'IRS Audit Protection',
      description: 'Every strategy backed by legal precedent',
      iconColor: 'from-green-500 to-green-600'
    },
    {
      title: 'CPA Coordination',
      description: 'We work with your existing accountant',
      iconColor: 'from-blue-500 to-blue-600'
    },
    {
      title: 'Money-Back Guarantee',
      description: 'Save more than our fee, or get refunded',
      iconColor: 'from-purple-500 to-purple-600'
    }
  ],
  socialProof = 'Join 1,200+ business owners saving millions',
  branding = {},
  isEditing = false,
  onUpdate
}) {
  const accentColor = branding?.secondary_color || '#AEFFE3';

  const handleUpdate = (key, value) => {
    if (onUpdate) {
      onUpdate({ [key]: value });
    }
  };

  const handleStatUpdate = (index, key, value) => {
    if (onUpdate) {
      const newStats = [...stats];
      newStats[index] = { ...newStats[index], [key]: value };
      onUpdate({ stats: newStats });
    }
  };

  const handleGuaranteeUpdate = (index, key, value) => {
    if (onUpdate) {
      const newGuarantees = [...guarantees];
      newGuarantees[index] = { ...newGuarantees[index], [key]: value };
      onUpdate({ guarantees: newGuarantees });
    }
  };

  const handlePartnerUpdate = (index, value) => {
    if (onUpdate) {
      const newPartners = [...partners];
      newPartners[index] = value;
      onUpdate({ partners: newPartners });
    }
  };

  return (
    <section className="relative bg-gradient-to-b from-gray-50 to-white py-20 px-6 overflow-hidden">
      {/* Subtle gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-transparent" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section header */}
        <div className="text-center mb-12">
          <div className="inline-block mb-4 px-4 py-2 bg-gradient-to-r from-blue-100 to-blue-50 rounded-full border border-blue-200">
            <EditableText
              value={sectionBadge}
              onChange={(text) => handleUpdate('sectionBadge', text)}
              isEditing={isEditing}
              element="span"
              className="text-sm font-semibold text-blue-900"
            />
          </div>
        </div>

        {/* Partner logos */}
        {partners && partners.length > 0 && (
          <div className="mb-16">
            <div className="flex flex-wrap justify-center items-center gap-6">
              {partners.map((partner, idx) => (
                <div
                  key={idx}
                  className="px-8 py-4 bg-white/80 backdrop-blur-md rounded-2xl border border-gray-200/50 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <EditableText
                    value={partner}
                    onChange={(text) => handlePartnerUpdate(idx, text)}
                    isEditing={isEditing}
                    element="div"
                    className="text-gray-600 font-semibold text-lg"
                  />
                </div>
              ))}

              {/* Add partner button (edit mode only) */}
              {isEditing && (
                <button
                  onClick={() => handleUpdate('partners', [...partners, 'New Partner'])}
                  className="px-6 py-4 border-2 border-dashed border-gray-300 rounded-2xl text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                >
                  + Add Partner
                </button>
              )}
            </div>
          </div>
        )}

        {/* Stats grid */}
        {stats && stats.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {stats.map((stat, idx) => (
              <div key={idx} className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent rounded-3xl blur-xl group-hover:blur-2xl transition-all" />
                <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl p-8 border border-gray-200/50 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
                  <div className="text-center">
                    <EditableText
                      value={stat.value}
                      onChange={(text) => handleStatUpdate(idx, 'value', text)}
                      isEditing={isEditing}
                      element="div"
                      className="text-5xl font-bold mb-3 bg-gradient-to-br from-blue-900 to-blue-600 bg-clip-text text-transparent"
                    />
                    <EditableText
                      value={stat.label}
                      onChange={(text) => handleStatUpdate(idx, 'label', text)}
                      isEditing={isEditing}
                      element="div"
                      className="text-gray-700 font-medium"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Guarantees grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {guarantees.map((guarantee, idx) => (
            <div
              key={idx}
              className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-gray-200/50 shadow-lg"
            >
              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-12 h-12 bg-gradient-to-br ${guarantee.iconColor} rounded-xl flex items-center justify-center`}>
                  {idx === 0 && (
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  )}
                  {idx === 1 && (
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  )}
                  {idx === 2 && (
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </div>
                <div>
                  <EditableText
                    value={guarantee.title}
                    onChange={(text) => handleGuaranteeUpdate(idx, 'title', text)}
                    isEditing={isEditing}
                    element="div"
                    className="font-semibold text-gray-900 mb-1"
                  />
                  <EditableText
                    value={guarantee.description}
                    onChange={(text) => handleGuaranteeUpdate(idx, 'description', text)}
                    isEditing={isEditing}
                    element="div"
                    className="text-sm text-gray-600"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Social proof */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 text-sm text-gray-600">
            <div className="flex -space-x-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 border-2 border-white" />
              ))}
            </div>
            <EditableText
              value={socialProof}
              onChange={(text) => handleUpdate('socialProof', text)}
              isEditing={isEditing}
              element="span"
              className="font-medium"
            />
          </div>
        </div>
      </div>

      {/* Edit mode overlay indicator */}
      {isEditing && (
        <div className="absolute top-4 left-4 px-3 py-1 bg-green-500/80 text-white text-xs font-medium rounded-full backdrop-blur-sm">
          Trust Block
        </div>
      )}
    </section>
  );
}
